import { apiClient } from "@/services/api-client";
import type { Notification } from "@/features/notifications/types/notification.types";

import type { AdaptedGroup } from "../types/group.types";

import type { AccountPlan, WaveAccountResult, WaveSide } from "../portfolio-preview/portfolio-preview.service";

/**
 * Portfolio Mode PROPOSAL card contract (operator M2 ruling 2026-09-01).
 *
 * Every shape here mirrors `fastapi_app/schemas/portfolio_preview.py`
 * (`WaveProposalResponse`, `AutoFireConsentResponse`) verbatim — the SAME
 * payload rides `Notification.extra_data` (plus `category`/`kind`/`note`),
 * the GET proposal route and the fire/decline responses. The card never
 * invents a field the server did not send.
 */

export const PROPOSAL_NOTIFICATION_CATEGORY = "portfolio_wave_propose";
export const PROPOSAL_KIND_PROPOSED = "portfolio_wave_propose";
export const PROPOSAL_KIND_AUTO_FIRED = "portfolio_wave_auto_fired";

export type WaveProposalStatus = "pending" | "fired" | "expired" | "declined";

export interface WaveProposalSignal {
  symbol: string;
  side: WaveSide;
  suggested_risk_points: number;
  suggested_target_points: number | null;
  /** Effective target (explicit, else 2R off the suggested risk). */
  target_points: number;
  /** Setup letter A/B/C. */
  quality: string | null;
  entry_price?: number | null;
  stop_loss?: number | null;
  take_profit?: number | null;
  confidence?: number | null;
  timeframe?: string | null;
  reasoning?: string | null;
  generated_at?: string | null;
}

export interface WaveFireResult {
  wave_id: string;
  group_id: number;
  mode_epoch: number;
  execution_root: string;
  side: WaveSide;
  benchmark: Record<string, unknown>;
  results: WaveAccountResult[];
}

export interface WaveProposal {
  proposal_id: number;
  group_id: number;
  wave_id: string;
  status: WaveProposalStatus;
  strategy_slug: string;
  mode_epoch: number;
  signal: WaveProposalSignal;
  plans: AccountPlan[];
  accounts_planned: number;
  orders_planned: number;
  created_at: string;
  expires_at: string;
  decided_at: string | null;
  decision_reason: string | null;
  /** The group's consent at read time (`null` when the reader did not load it). */
  auto_fire_consent: boolean | null;
  /** `wave_result_dict` of the fire, once fired. */
  fire_result: WaveFireResult | null;
}

/** Who last flipped the consent; `display_name` is "you" for the requester, else a first name / `member #N`. */
export interface AutoFireConsentActor {
  user_id: number;
  display_name: string;
}

/** `GET|PATCH /portfolio-wave/auto-fire-consent` (`AutoFireConsentResponse`). */
export interface AutoFireConsent {
  group_id: number;
  auto_fire_consent: boolean;
  consented_at: string | null;
  actor_user_id: number | null;
  portfolio_auto_fire_consent: boolean;
  portfolio_auto_fire_consent_at: string | null;
  portfolio_auto_fire_consent_actor: AutoFireConsentActor | null;
}

/**
 * The consent triple as the UI reads it — the same three facts ride the group
 * payload (`portfolio_auto_fire_consent*` on `GET /trading-groups[/{id}]`)
 * and the consent route's response. `at` is stamped on EVERY flip, so the
 * later stamp is always the newer truth (`newerConsent`).
 */
export interface AutoFireConsentState {
  on: boolean;
  at: string | null;
  actor: AutoFireConsentActor | null;
}

/** `null` when the payload predates the fields — the reader then GETs the consent route. */
export function consentStateFromGroup(
  group: Pick<AdaptedGroup, "portfolioAutoFireConsent" | "portfolioAutoFireConsentAt" | "portfolioAutoFireConsentActor">,
): AutoFireConsentState | null {
  if (group.portfolioAutoFireConsent == null) return null;
  return {
    on: group.portfolioAutoFireConsent,
    at: group.portfolioAutoFireConsentAt ?? null,
    actor: group.portfolioAutoFireConsentActor ?? null,
  };
}

export function consentStateFromRow(row: AutoFireConsent): AutoFireConsentState {
  return {
    on: row.portfolio_auto_fire_consent,
    at: row.portfolio_auto_fire_consent_at,
    actor: row.portfolio_auto_fire_consent_actor,
  };
}

/** The later-stamped of two consent reads (a PATCH answer vs a group payload that has not refetched yet). */
export function newerConsent(
  a: AutoFireConsentState | null,
  b: AutoFireConsentState | null,
): AutoFireConsentState | null {
  if (!a || !b) return a ?? b;
  const stamp = (state: AutoFireConsentState) => (state.at ? Date.parse(state.at) : 0);
  return stamp(b) > stamp(a) ? b : a;
}

/** `POST /portfolio-wave/fire {proposal_id}` → the wave result + the row's status. */
export interface FireProposalResult extends WaveFireResult {
  proposal_id: number | null;
  proposal_status: string | null;
}

/** Backend `REASON_SUPERSEDED` — a newer signal replaced this card. */
const SUPERSEDED_MARKER = /superseded/i;

/**
 * The card's SIX honest states. `expired` splits into superseded (a newer
 * card exists for this group) and plain expired (TTL / epoch) because the
 * member's next step differs: look for the newer card vs wait for a signal.
 */
export type ProposalCardState =
  | "pending"
  | "fired"
  | "declined"
  | "expired"
  | "superseded";

export function proposalCardState(
  proposal: Pick<WaveProposal, "status" | "decision_reason" | "expires_at">,
  now: number = Date.now(),
): ProposalCardState {
  if (proposal.status === "fired") return "fired";
  if (proposal.status === "declined") return "declined";
  if (proposal.status === "expired") {
    return SUPERSEDED_MARKER.test(proposal.decision_reason ?? "") ? "superseded" : "expired";
  }
  // Still `pending` on the row but past its TTL: the server marks it expired
  // on the next touch; the card says so now rather than offering a dead tap.
  const expiresAt = Date.parse(proposal.expires_at);
  if (Number.isFinite(expiresAt) && expiresAt <= now) return "expired";
  return "pending";
}

export function isWaveProposal(value: unknown): value is WaveProposal {
  const p = value as Partial<WaveProposal> | null | undefined;
  return (
    !!p &&
    typeof p === "object" &&
    !Array.isArray(p) &&
    typeof p.proposal_id === "number" &&
    typeof p.group_id === "number" &&
    typeof p.status === "string" &&
    !!p.signal &&
    typeof p.signal === "object" &&
    Array.isArray(p.plans) &&
    typeof p.expires_at === "string"
  );
}

export interface ProposalNotificationRef {
  groupId: number;
  proposalId: number;
  kind: string;
  /** The payload as it rode the notification — first paint before the GET lands. */
  seed: WaveProposal | null;
  createdAt: string;
  notificationId: Notification["id"];
}

/**
 * Reads a proposal reference off a notification (live WS frame or REST row).
 * `null` for anything that is not the proposal lane — callers filter with it.
 */
export function readProposalNotification(
  notification: Pick<Notification, "id" | "created_at" | "extra_data">,
): ProposalNotificationRef | null {
  const extra = notification.extra_data as Record<string, unknown> | null | undefined;
  if (!extra || extra.category !== PROPOSAL_NOTIFICATION_CATEGORY) return null;
  const groupId = Number(extra.group_id);
  const proposalId = Number(extra.proposal_id);
  if (!Number.isInteger(groupId) || !Number.isInteger(proposalId) || proposalId <= 0) return null;
  return {
    groupId,
    proposalId,
    kind: typeof extra.kind === "string" ? extra.kind : PROPOSAL_KIND_PROPOSED,
    seed: isWaveProposal(extra) ? (extra as unknown as WaveProposal) : null,
    createdAt: notification.created_at,
    notificationId: notification.id,
  };
}

/** The deep link the backend stamps as `action_url` on every proposal card. */
export function proposalDeepLink(groupId: number, proposalId: number): string {
  return `/dashboard?group=${groupId}&proposal=${proposalId}`;
}

/**
 * Open a proposal deep link from a non-React caller (the WS toast). On the
 * dashboard itself a pushState is enough — Next syncs `useSearchParams` from
 * it — so the card opens in place; anywhere else it is a real navigation.
 */
export function openProposalDeepLink(href: string): void {
  if (typeof window === "undefined") return;
  if (window.location.pathname === "/dashboard") {
    window.history.pushState(window.history.state, "", href);
    return;
  }
  window.location.assign(href);
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const waveProposalQueryKey = (groupId: number, proposalId: number) =>
  ["portfolio-proposal", groupId, proposalId] as const;
export const autoFireConsentQueryKey = (groupId: number) =>
  ["portfolio-auto-fire-consent", groupId] as const;
/** Prefix for the lane-truth preview reads the card makes (see `fetchProposalLane`). */
export const portfolioPreviewQueryKey = (groupId: number) =>
  ["portfolio-preview", groupId] as const;

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export async function fetchWaveProposal(groupId: number, proposalId: number): Promise<WaveProposal> {
  const response = await apiClient.get<WaveProposal>(
    `/trading-groups/${groupId}/portfolio-wave/proposals/${proposalId}`,
  );
  if (!isWaveProposal(response.data)) {
    throw new Error("Proposal payload is not a wave proposal");
  }
  return response.data;
}

export async function fireWaveProposal(groupId: number, proposalId: number): Promise<FireProposalResult> {
  const response = await apiClient.post<FireProposalResult>(
    `/trading-groups/${groupId}/portfolio-wave/fire`,
    { proposal_id: proposalId },
  );
  return response.data;
}

export async function declineWaveProposal(groupId: number, proposalId: number): Promise<WaveProposal> {
  const response = await apiClient.post<WaveProposal>(
    `/trading-groups/${groupId}/portfolio-wave/proposals/${proposalId}/decline`,
    {},
  );
  return response.data;
}

export async function fetchAutoFireConsent(groupId: number): Promise<AutoFireConsent> {
  const response = await apiClient.get<AutoFireConsent>(
    `/trading-groups/${groupId}/portfolio-wave/auto-fire-consent`,
  );
  return response.data;
}

export async function setAutoFireConsent(groupId: number, consent: boolean): Promise<AutoFireConsent> {
  const response = await apiClient.patch<AutoFireConsent>(
    `/trading-groups/${groupId}/portfolio-wave/auto-fire-consent`,
    { consent },
  );
  return response.data;
}

/**
 * Lane truth for THIS proposal's signal: the server-owned `execution_status`
 * sentence + `execution_enabled` from the preview route (composed for the
 * proposal's own geometry). The card renders the sentence verbatim and
 * disables Fire while the lane is dark — the client never guesses.
 */
export interface ProposalLane {
  execution_status: string;
  execution_enabled: boolean;
  mode_note: string | null;
}

export async function fetchProposalLane(
  groupId: number,
  signal: Pick<WaveProposalSignal, "symbol" | "side" | "suggested_risk_points" | "target_points" | "quality">,
): Promise<ProposalLane> {
  const query = new URLSearchParams({
    symbol: signal.symbol,
    side: signal.side,
    risk_points: String(signal.suggested_risk_points),
  });
  if (signal.target_points > 0) query.set("target_points", String(signal.target_points));
  if (signal.quality) query.set("quality", signal.quality);
  const response = await apiClient.get<ProposalLane & { group_id: number }>(
    `/trading-groups/${groupId}/portfolio-preview?${query.toString()}`,
  );
  const lane = response.data;
  if (!lane || typeof lane.execution_status !== "string") {
    throw new Error("Preview payload carries no execution_status");
  }
  return {
    execution_status: lane.execution_status,
    execution_enabled: lane.execution_enabled === true,
    mode_note: lane.mode_note ?? null,
  };
}

/** Member-language detail off an ApiClientError (`detail` string) or a plain Error. */
export function proposalErrorMessage(error: unknown, fallback: string): string {
  const detail = (error as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail && typeof detail === "object" && typeof (detail as { message?: unknown }).message === "string") {
    return (detail as { message: string }).message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
